import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from './_cn';

const sizes = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
};

const TWModal = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'lg',
  initialFocus,
  dismissible = true,
  showCloseButton = false,
  className = '',
  panelClassName = '',
  bodyClassName = '',
}) => (
  <Transition.Root show={open} as={Fragment}>
    <Dialog
      as="div"
      className={cn('relative z-[60]', className)}
      initialFocus={initialFocus}
      onClose={dismissible ? onClose : () => {}}
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
      </Transition.Child>

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-3 text-left sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className={cn(
                'relative w-full transform overflow-hidden rounded-t-2xl bg-white text-left shadow-xl transition-all dark:bg-slate-900 sm:my-8 sm:rounded-md',
                sizes[size] || sizes.lg,
                panelClassName,
              )}
            >
              {title || description || showCloseButton ? (
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700">
                  <div className="min-w-0 flex-1">
                    {title ? (
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold text-slate-900 dark:text-slate-100"
                      >
                        {title}
                      </Dialog.Title>
                    ) : null}
                    {description ? (
                      <Dialog.Description className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                        {description}
                      </Dialog.Description>
                    ) : null}
                  </div>
                  {showCloseButton ? (
                    <button
                      type="button"
                      onClick={onClose}
                      className="-m-1 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                      aria-label="Close"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden />
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className={cn('px-5 py-4', bodyClassName)}>{children}</div>
              {footer ? (
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-slate-700 dark:bg-slate-800/80">
                  {footer}
                </div>
              ) : null}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

export default TWModal;
